import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../api/axiosInstance";
import ProjectMultiSelect from "../components/ProjectMultiSelect";
import { useToast } from "../context/ToastContext";
import { humanizeError } from "../utils/userFriendlyError";

function TeamManagement({ user }) {
  const { toast } = useToast();
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("OPS");
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [orgProjects, setOrgProjects] = useState([]);
  const [editingProjectsUserId, setEditingProjectsUserId] = useState(null);
  const [editingProjectIds, setEditingProjectIds] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchProjects();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/users");
      setOrgUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get("/projects");
      setOrgProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const orgId = userData.user?.org_id || userData.organization?.id;
      
      if (!orgId) {
        toast.error("Organization ID not found. Please log in again.");
        return;
      }

      const payload = {
        org_id: orgId,
        first_name: firstName,
        middle_name: middleName || null,
        last_name: lastName,
        email: email,
        role: role,
      };
      if (role === "OPS" && selectedProjectIds.length > 0) {
        payload.project_ids = selectedProjectIds;
      }
      const response = await axiosInstance.post("/users", payload);
      setOrgUsers([...orgUsers, response.data]);
      setShowAddUserModal(false);
      setFirstName("");
      setMiddleName("");
      setLastName("");
      setEmail("");
      setRole("OPS");
      setSelectedProjectIds([]);
    } catch (error) {
      console.error("Error adding user:", error);
      toast.error(humanizeError(error));
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axiosInstance.put(`/users/${userId}`, {
        role: newRole
      });
      if (newRole === "OPS") {
        setEditingProjectsUserId(userId);
        setEditingProjectIds([]);
      } else {
        if (editingProjectsUserId === userId) {
          setEditingProjectsUserId(null);
          setEditingProjectIds([]);
        }
      }
      fetchUsers();
    } catch (error) {
      console.error("Error updating user role:", error);
      toast.error(humanizeError(error));
    }
  };

  const handleUpdateUserProjects = async (userId, projectIds) => {
    try {
      await axiosInstance.put(`/users/${userId}`, {
        project_ids: projectIds
      });
      setEditingProjectsUserId(null);
      setEditingProjectIds([]);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user projects:", error);
      toast.error(humanizeError(error));
    }
  };

  const handleUpdateUserStatus = async (userId, newStatus) => {
    try {
      await axiosInstance.put(`/users/${userId}`, {
        status: newStatus
      });
      fetchUsers();
    } catch (error) {
      console.error("Error updating user status:", error);
      toast.error(humanizeError(error));
    }
  };

  const isAdmin = user?.role?.toLowerCase() === "admin";

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20">
      <Header user={user} />
      <div className="flex flex-1">
      <Sidebar />
        <main className="flex-1 p-8 bg-gradient-to-b from-transparent to-emerald-50/10">
          <div className="max-w-7xl mx-auto w-full">
            {/* Team Management Header Section */}
            <div className="mb-8 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-blue-500/5 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-emerald-100/50 shadow-xl">
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <h1 className="text-3xl font-extrabold bg-gradient-to-r from-gray-900 via-emerald-700 to-gray-900 bg-clip-text text-transparent mb-3">
                      Team Management
                    </h1>
                    <p className="text-gray-600 text-lg font-medium">Manage organization users and access levels</p>
              </div>
              {isAdmin && (
                <button
                  onClick={() => setShowAddUserModal(true)}
                      className="btn-primary flex items-center gap-3 px-6 py-3.5 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
                >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                      <span className="font-semibold">Add User</span>
                </button>
              )}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-20">
                <div className="relative w-20 h-20 mx-auto mb-6">
                  <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-transparent border-t-emerald-600 rounded-full animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Loading Users</h3>
                <p className="text-gray-600 font-medium">Please wait while we fetch the team members...</p>
              </div>
            ) : orgUsers.length === 0 ? (
              <div className="text-center py-20 card max-w-lg mx-auto bg-gradient-to-br from-white via-emerald-50/20 to-blue-50/20 border-emerald-200/50 shadow-xl">
                <div className="w-24 h-24 bg-gradient-to-br from-emerald-100 to-blue-100 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <svg className="w-14 h-14 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">No users yet</h3>
                <p className="text-gray-600 mb-8 text-lg font-medium">Add users to your organization</p>
                {isAdmin && (
                  <button
                    onClick={() => setShowAddUserModal(true)}
                    className="btn-primary px-8 py-4 text-base font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
                  >
                    Add First User
                  </button>
                )}
              </div>
            ) : (
              <div className="card bg-gradient-to-br from-white via-emerald-50/20 to-white border-emerald-200/50 shadow-lg">
                <div className="space-y-4">
                  {orgUsers.map((orgUser) => (
                    <div
                      key={orgUser.id}
                      className="p-6 rounded-2xl border-2 border-gray-200 hover:border-emerald-400 hover:bg-gradient-to-br hover:from-emerald-50/50 hover:to-blue-50/30 transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-5 flex-1">
                          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 via-emerald-600 to-blue-600 rounded-2xl flex items-center justify-center text-white text-lg font-bold shadow-lg">
                            {orgUser.first_name?.charAt(0)}{orgUser.last_name?.charAt(0)}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-1">
                              {orgUser.first_name} {orgUser.middle_name ? `${orgUser.middle_name} ` : ''}{orgUser.last_name}
                            </h3>
                            <p className="text-sm text-gray-600 font-medium">{orgUser.email}</p>
                            {orgUser.g_number && (
                              <p className="text-xs text-gray-500 font-medium mt-1">G#: {orgUser.g_number}</p>
                            )}
                            {orgUser.role === "OPS" && orgUser.projects?.length > 0 && editingProjectsUserId !== orgUser.id && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {orgUser.projects.map((proj) => (
                                  <span
                                    key={proj.id}
                                    className="inline-flex items-center px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold border border-blue-200"
                                  >
                                    {proj.title}
                                  </span>
                                ))}
                              </div>
                            )}
                            {orgUser.role === "OPS" && (!orgUser.projects || orgUser.projects.length === 0) && editingProjectsUserId !== orgUser.id && (
                              <p className="text-xs text-gray-400 mt-2 italic">No projects assigned</p>
                            )}
                          </div>
                          {(!isAdmin || orgUser.id === user?.id) && (
                            <div className="flex items-center gap-3">
                              <span className={`badge text-xs px-3 py-1.5 font-semibold ${
                                orgUser.role === 'ADMIN' ? 'badge-success' : 'badge-info'
                              }`}>
                                {orgUser.role}
                              </span>
                              <span className={`badge text-xs px-3 py-1.5 font-semibold ${
                                orgUser.status === 'ACTIVE' ? 'badge-success' : 'badge-warning'
                              }`}>
                                {orgUser.status}
                              </span>
                            </div>
                          )}
                        </div>
                        {isAdmin && orgUser.id !== user?.id && (
                          <div className="flex items-center gap-3 ml-4">
                            <select
                              value={orgUser.role}
                              onChange={(e) => handleUpdateUserRole(orgUser.id, e.target.value)}
                              className="px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white hover:border-emerald-300 transition-all shadow-sm"
                            >
                              <option value="OPS">Ops</option>
                              <option value="ADMIN">Admin</option>
                              <option value="ADVISOR">Advisor</option>
                            </select>
                            <select
                              value={orgUser.status}
                              onChange={(e) => handleUpdateUserStatus(orgUser.id, e.target.value)}
                              className="px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white hover:border-emerald-300 transition-all shadow-sm"
                            >
                              <option value="ACTIVE">Active</option>
                              <option value="INACTIVE">Inactive</option>
                            </select>
                            {orgUser.role === "OPS" && editingProjectsUserId !== orgUser.id && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingProjectsUserId(orgUser.id);
                                  setEditingProjectIds(orgUser.projects?.map((p) => p.id) || []);
                                }}
                                className="px-3 py-2.5 border-2 border-emerald-200 rounded-xl text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300 transition-all shadow-sm"
                                title="Edit project access"
                              >
                                Edit Projects
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {isAdmin && editingProjectsUserId === orgUser.id && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <label className="block text-sm font-bold text-gray-700 mb-2">
                            Project Access
                          </label>
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <ProjectMultiSelect
                                projects={orgProjects}
                                selectedIds={editingProjectIds}
                                onChange={setEditingProjectIds}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleUpdateUserProjects(orgUser.id, editingProjectIds)}
                              className="px-4 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-semibold hover:bg-emerald-600 transition-all shadow-sm"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingProjectsUserId(null);
                                setEditingProjectIds([]);
                              }}
                              className="px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all shadow-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddUserModal(false);
          setFirstName("");
          setMiddleName("");
          setLastName("");
          setEmail("");
          setRole("OPS");
          setSelectedProjectIds([]);
        }}>
          <div className="modal-content bg-gradient-to-br from-white via-emerald-50/10 to-blue-50/10 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 sm:mb-6 pb-4 sm:pb-5 border-b border-emerald-200/50">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h2 className="text-xl sm:text-3xl font-extrabold bg-gradient-to-r from-gray-900 via-emerald-700 to-blue-700 bg-clip-text text-transparent">
                  Add New User
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowAddUserModal(false);
                  setFirstName("");
                  setMiddleName("");
                  setLastName("");
                  setEmail("");
                  setRole("OPS");
                  setSelectedProjectIds([]);
                }}
                className="btn-ghost p-2 rounded-lg hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleAddUser} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="input-modern text-base"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    Middle Name
                  </label>
                  <input
                    type="text"
                    value={middleName}
                    onChange={(e) => setMiddleName(e.target.value)}
                    className="input-modern text-base"
                    placeholder="Middle name (optional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="input-modern text-base"
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="input-modern text-base"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  Role
                </label>
                <select
                  value={role}
                  onChange={(e) => {
                    setRole(e.target.value);
                    if (e.target.value !== "OPS") {
                      setSelectedProjectIds([]);
                    }
                  }}
                  className="input-modern text-base"
                >
                  <option value="OPS">Ops</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
              {role === "OPS" && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    Project Access
                  </label>
                  <ProjectMultiSelect
                    projects={orgProjects}
                    selectedIds={selectedProjectIds}
                    onChange={setSelectedProjectIds}
                  />
                  <p className="text-xs text-gray-400 mt-1.5">Select projects this user can access</p>
                </div>
              )}
              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUserModal(false);
                    setFirstName("");
                    setMiddleName("");
                    setLastName("");
                    setEmail("");
                    setRole("OPS");
                  }}
                  className="btn-secondary flex-1 py-3 sm:py-3.5 font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 py-3 sm:py-3.5 font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamManagement;
